import styled from "styled-components";
import { Link } from "wouter";

import { breakpoints, colors } from "definitions";

const muted = "#6B7A93";
const line = "#D6DEEA";

// En las pantallas del jurado va primero, montado sobre el hero como el filtro
// de las demás páginas (`$overHero`): el mismo ancho, sombra y radio que
// `HeaderSectionContainer`. En la home va entre las secciones, sin montarse.
export const WorkflowContainer = styled.section`
  width: 95%;
  max-width: 124.5rem;
  margin: ${({ $overHero }) => ($overHero ? "-4.6rem 0 0 0" : "0")};
  padding: 18px 22px 16px;
  background-color: ${colors.background.white};
  border-radius: 6px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.15);
  font-family: Lato;
  box-sizing: border-box;

  ${breakpoints.tablet} {
    width: 80%;
  }

  ${breakpoints.phablet} {
    width: 95%;
  }
`;

export const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 6px 16px;
  margin-bottom: 18px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${muted};
`;

export const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  border: 0;
  background: none;
  font-family: Lato;
  font-size: 13px;
  font-weight: 700;
  color: ${colors.fonts.yellowGreen};
  cursor: pointer;

  &:hover {
    color: ${colors.navigation.brightBlue};
  }

  &:focus-visible {
    outline: 2px solid ${colors.navigation.brightBlue};
    outline-offset: 2px;
    border-radius: 3px;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 200ms ease;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  ${breakpoints.phablet} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Circle = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 2px solid ${line};
  color: ${muted};
  background-color: ${colors.background.white};
  box-sizing: border-box;

  svg {
    width: 22px;
    height: 22px;
  }
`;

// Horizontal: cada paso centrado en su columna y la línea que lo une al
// anterior sale de su `::before`. En el celular pasa a vertical, con el
// círculo a la izquierda y la línea bajando entre círculos.
export const Step = styled.li`
  position: relative;
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 6px;
  padding: 0 6px;
  text-align: center;

  & + &::before {
    content: "";
    position: absolute;
    top: 22px;
    left: calc(-50% + 26px);
    right: calc(50% + 26px);
    height: 2px;
    background-color: ${line};
  }

  &[data-highlight] ${Circle} {
    background-color: ${colors.background.gray};
    border-color: ${colors.background.gray};
    color: ${colors.fonts.white};
    box-shadow: 0 0 0 5px rgba(0, 110, 223, 0.14);
  }

  ${breakpoints.phablet} {
    grid-template-columns: 44px minmax(0, 1fr);
    justify-items: start;
    column-gap: 14px;
    row-gap: 2px;
    padding: 0 0 16px;
    text-align: left;

    & > ${Circle} {
      grid-row: 1 / span 6;
    }

    & + &::before {
      top: -16px;
      left: 21px;
      right: auto;
      width: 2px;
      height: 16px;
    }
  }
`;

const nameStyles = `
  font-size: 14px;
  font-weight: 700;
  color: ${colors.fonts.darkGray};
`;

export const StepName = styled.span`
  ${nameStyles}

  ${Step}[data-highlight] & {
    color: ${colors.background.gray};
  }
`;

export const StepLink = styled(Link)`
  ${nameStyles}
  text-decoration: none;

  &:hover {
    color: ${colors.navigation.brightBlue};
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid ${colors.navigation.brightBlue};
    outline-offset: 2px;
  }
`;

export const HereBadge = styled.span`
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.fonts.white};
  background-color: ${colors.background.gray};
  border-radius: 999px;
  padding: 2px 8px;
`;

export const StepRole = styled.span`
  font-size: 12px;
  color: ${muted};
`;

// El estado en el que el paso deja la postulación: la flecha lo lee como
// "pasa a".
export const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.fonts.yellowGreen};
  background-color: #e8f1fd;
  border-radius: 999px;
  padding: 2px 8px;

  &::before {
    content: "→ ";
    font-weight: 400;
    color: ${muted};
  }
`;

export const StepDate = styled.span`
  font-size: 12px;
  color: ${colors.fonts.darkGray};
  font-variant-numeric: tabular-nums;
`;

export const Explain = styled.span`
  max-width: 22ch;
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.45;
  color: ${colors.fonts.darkGray};

  ${breakpoints.phablet} {
    max-width: none;
  }
`;

export const DetailFoot = styled.p`
  margin: 16px 0 0;
  padding-top: 12px;
  border-top: 1px solid ${line};
  font-size: 12px;
  color: ${muted};
`;
